import { NoDataComponent, CustomTabs as CustomTabsComponent } from '@/shared/ui/components';
import { isFalsyOrEmpty } from '@/shared/lib';
import classNames from 'classnames';
import _ from 'lodash';
import { t } from 'ttag';
import { useHowToVideos } from './useHowtoVideos';
import { handleUrlYoutube } from './handler';
import type { HowToVideosProps } from './howToVideos.types';
import './howToVideos.scss';

const CustomTabs = CustomTabsComponent as any;

/**
 * Renders the how-to videos tab of the support page as Bioflux and Biocore sub-tabs. Only mounts when the tab is active, and both sub-tabs share the content renderer that lists the selectable videos for the active device, embeds the selected one as a YouTube iframe and falls back to the no-data placeholder when no playable video exists.
 *
 * @param isActive - Whether the how-to videos tab is currently selected.
 * @param howtoVideos - Video entries bucketed by device under bioflux and biotres keys.
 * @returns The how-to videos sub-tab set, or a falsy value when inactive.
 */
const HowToVideos = ({ isActive, howtoVideos }: HowToVideosProps) => {
  const { state, setState, videoSelect, navigateTab } = useHowToVideos({ howtoVideos });

  /**
   * Renders the video list, player and description for the active Bioflux or Biotres how-to sub-tab. When that device's bucket is empty or every entry lacks a url the no-data placeholder is shown, otherwise each video with a url gets a clickable title that stores the selection per device, and the selected video is embedded through the YouTube url converter alongside its title and description.
   *
   * @returns The body of the active how-to video sub-tab.
   */
  const renderContent = () => {
    const key = state.activeTab === '1' ? 'bioflux' : 'biotres';
    /**
     * Flag telling whether the active device's how-to video list is unusable. Derived from that device's bucket of the video data being falsy or empty, or from every entry in it lacking a url.
     *
     * @returns True when no playable video exists for the active device.
     */
    const isInvalidVideo =
      isFalsyOrEmpty(howtoVideos?.[key]) || _.every(howtoVideos?.[key], (x) => !x.url);
    if (isInvalidVideo) {
      return (
        <div className="support-tab__body">
          <NoDataComponent />
        </div>
      );
    }

    return (
      <div className="support-tab__body">
        <div className="small-videos">
          {_.map(
            howtoVideos?.[state.activeTab === '1' ? 'bioflux' : 'biotres'],
            (item, index) =>
              item.url && (
                <div
                  key={index}
                  aria-hidden
                  onClick={() => {
                    setState(
                      state.activeTab === '1'
                        ? { videoBiofluxSelected: item }
                        : { videoBiotresSelected: item },
                    );
                  }}
                  className={classNames(
                    'small-video',
                    item.urlId === videoSelect?.urlId && 'active',
                  )}
                >
                  <span>{item.title}</span>
                </div>
              ),
          )}
        </div>

        <div className="video-space">
          <div className="large-video">
            {/* oxlint-disable-next-line jsx-a11y/iframe-has-title */}
            <iframe
              title={videoSelect?.title}
              width="100%"
              height="100%"
              src={handleUrlYoutube(videoSelect?.url)}
              frameBorder="0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>

        <div className="description">
          <h4>{videoSelect?.title}</h4>
          <span>{videoSelect?.description}</span>
        </div>
      </div>
    );
  };

  return (
    isActive && (
      <div className="support-tab how-to-videos-tab">
        <CustomTabs
          activeKey={state.activeTab}
          onChange={navigateTab}
          items={[
            {
              label: <div className="title-tab">{t`Bioflux - How to videos`}</div>,
              key: '1',
              children: renderContent(),
            },
            {
              label: <div className="title-tab">{t`Biocore - How to videos`}</div>,
              key: '2',
              children: renderContent(),
            },
          ]}
        />
      </div>
    )
  );
};

export default HowToVideos;
